import React, { useMemo, useState, useEffect, useRef } from 'react';
import { FieldErrors, UseFormReturn } from 'react-hook-form';
import { getTimeForReservation } from '@/lib/utils';
import { RESERVE_TEXTS } from '@/lib/utils-reserve';
import { SaveReservationData } from '@/lib/utils-reserve';
import { FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Office } from '@gettactic/api';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { useTimeInputMask } from '@gettactic/helpers/src/input/useTimeInputMask';
import { generateTimeOptions, loadTimeOptions, TimeOption } from './reserveTimeHelpers';
import { format } from 'date-fns';
import { scrollToSelectedValue } from '../utils/scrollToSelectedValue';

type ReserveTimeProps = {
  form: UseFormReturn<any>;
  defaultValue: Date;
  weekDates: Date[];
  setStartTime: React.Dispatch<React.SetStateAction<Date>>;
  office: Office | null;
  errors: FieldErrors<SaveReservationData>;
};

export default function ReserveFieldTime({
  form,
  defaultValue,
  weekDates,
  setStartTime,
  office,
  errors
}: ReserveTimeProps) {
  const baseDate = weekDates[0] || new Date();

  const timeOptions = useMemo(() => {
    const options = generateTimeOptions(baseDate);
    const hasToAddDefaultValue = defaultValue && !options.find((option) => option.value.getTime() === defaultValue.getTime());
    if(hasToAddDefaultValue) {
      options.push({
        label: format(defaultValue, 'h:mmaaa'),
        value: defaultValue
      });
    }
    return options;
  }, [baseDate, defaultValue]);

  // State to track the selected option
  const [selectedOption, setSelectedOption] = useState<TimeOption | null>(null);

  // Update selected option when defaultValue changes
  const currentlyOnForm = form.watch('time');
  const defaultValueIsSet = useRef(false);
  useEffect(() => {
    if(defaultValueIsSet.current) {
      return;
    }
    if(defaultValue) {
      const currentValue = currentlyOnForm || defaultValue;
      const matchingOption = timeOptions.find(
        (option) =>
          option.value.getHours() === currentValue.getHours() &&
          option.value.getMinutes() === currentValue.getMinutes()
      );
      setSelectedOption(matchingOption || null);
      defaultValueIsSet.current = true;
    }
  }, [defaultValue, timeOptions, form]);

  const mask = useTimeInputMask();

  // Function to scroll to selected value when menu opens
  const handleMenuOpen = () => {
    scrollToSelectedValue(selectedOption);
  };

  return (
    <>
      <FormField
        control={form.control}
        name="time"
        defaultValue={defaultValue}
        render={({ field }) => (
          <FormItem>
            <AsyncSelect
              menuPortalTarget={document.body}
              menuPosition={'fixed'}
              onMenuOpen={handleMenuOpen}
              components={{
                SingleValue: (props) => (
                  <components.SingleValue {...props}>
                    {props.data.label}
                  </components.SingleValue>
                ),
                Input: (props) => (
                  <components.Input {...props} size={undefined} {...mask} />
                )
              }}
              onChange={(selectedTimeOption: TimeOption | null, event) => {
                if (!weekDates[0]) {
                  // prevent calculations with an undefined date
                  return;
                }

                if (selectedTimeOption) {
                  const newTime = getTimeForReservation(
                    weekDates[0],
                    selectedTimeOption.value,
                    office
                  );

                  field.onChange(newTime, event);
                  setStartTime(newTime);
                  setSelectedOption(selectedTimeOption);
                }
              }}
              value={selectedOption}
              loadOptions={(filter, callback) =>
                callback(loadTimeOptions(filter, baseDate, timeOptions))
              }
              defaultOptions={timeOptions}
              placeholder={RESERVE_TEXTS.selectTime}
              classNamePrefix="react-select"
            />
            <FormMessage />
          </FormItem>
        )}
      />
      {errors.time && (
        <p className="text-primary">Specify a valid start time</p>
      )}
    </>
  );
}
